import { calculateTennisStats } from '../utils/statCalculations';

/**
 * Deterministic threshold-rule insight cards for a Tennis player's season
 * stats (sc-339 / Tennis Phase 5). Pure function over raw sport_stats totals
 * so it's cheap to test and reuse (player card page today; Coach Steve or a
 * practice-plan nudge later).
 */
export interface TennisInsight {
  id: string;
  message: string;
  /** Practice theme(s) this insight should link to (src/data/sportRulesets.ts Tennis themes). */
  practiceThemes: string[];
}

function pct(num: number, den: number): number | null {
  return den > 0 ? (num / den) * 100 : null;
}

export function getTennisInsights(raw: Record<string, number>): TennisInsight[] {
  const calc = calculateTennisStats(Object.entries(raw).map(([stat_key, stat_value]) => ({ stat_key, stat_value })));
  const insights: TennisInsight[] = [];

  const toPct = (ratio: number | undefined): number | null => (ratio !== undefined ? ratio * 100 : null);
  const secondServeWinPct = toPct(calc.SecondServeWinPct);
  const firstServePct = toPct(calc.FirstServePct);
  const firstServeWinPct = toPct(calc.FirstServeWinPct);
  const firstStrikePct = toPct(calc.FirstStrikePct);
  const netWinPct = toPct(calc.NetWinPct);

  if (secondServeWinPct !== null && secondServeWinPct < 40) {
    insights.push({
      id: 'low-2nd-serve-win',
      message: `2nd serve win % is ${Math.round(secondServeWinPct)}% — well below a healthy range.`,
      practiceThemes: ['Serve Day'],
    });
  }

  if (firstServePct !== null && firstServePct > 70 && firstServeWinPct !== null && firstServeWinPct < 55) {
    insights.push({
      id: 'passive-1st-serve',
      message: `1st serve % is high (${Math.round(firstServePct)}%) but only winning ${Math.round(firstServeWinPct)}% of those points — the serve may be too conservative.`,
      practiceThemes: ['Serve Day'],
    });
  }

  const setsPlayed = (raw.SetsWon ?? 0) + (raw.SetsLost ?? 0);
  const dfPerSet = setsPlayed > 0 ? (raw.DoubleFaults ?? 0) / setsPlayed : null;
  if (dfPerSet !== null && dfPerSet > 2.5) {
    insights.push({
      id: 'high-double-faults',
      message: `Averaging ${dfPerSet.toFixed(1)} double faults per set.`,
      practiceThemes: ['Serve Day'],
    });
  }

  if (calc.WUERatio !== undefined && calc.WUERatio < 0.6) {
    insights.push({
      id: 'low-wue-ratio',
      message: `Winner-to-unforced-error ratio is ${calc.WUERatio.toFixed(1)} — errors are outpacing winners.`,
      practiceThemes: ['Groundstrokes Day'],
    });
  }

  const unforcedErrors = raw.UnforcedErrors ?? 0;
  const longErrorPct = pct(raw.UnforcedErrors_Long ?? 0, unforcedErrors);
  if (longErrorPct !== null && longErrorPct > 60) {
    insights.push({
      id: 'long-errors',
      message: `${Math.round(longErrorPct)}% of unforced errors are going long.`,
      practiceThemes: ['Groundstrokes Day'],
    });
  }

  // Total points played = points served + points returned (ServePts_1st counts every service
  // point, one first-serve attempt each; RetPts counts every point returned).
  const totalPoints = (raw.ServePts_1st ?? 0) + (raw.RetPts ?? 0);
  const netApproachPct = pct(raw.NetPts ?? 0, totalPoints);
  if (netApproachPct !== null && netApproachPct < 10 && netWinPct !== null && netWinPct > 60) {
    insights.push({
      id: 'under-using-net',
      message: `Approaching net on only ${Math.round(netApproachPct)}% of points but winning ${Math.round(netWinPct)}% of those — there's room to come forward more.`,
      practiceThemes: ['Net Play Day'],
    });
  }

  if (firstStrikePct !== null && firstStrikePct < 45) {
    insights.push({
      id: 'weak-first-strike',
      message: `Winning only ${Math.round(firstStrikePct)}% of short (1-3 shot) points.`,
      practiceThemes: ['Serve Day', 'Match Day'],
    });
  }

  const longRallyWinPct = pct(raw.Rally_10PlusWon ?? 0, raw.Rally_10Plus ?? 0);
  if (longRallyWinPct !== null && longRallyWinPct > 60 && firstStrikePct !== null && firstStrikePct < 45) {
    insights.push({
      id: 'grind-it-out',
      message: `Winning ${Math.round(longRallyWinPct)}% of long (10+ shot) rallies but struggling to win points early — point construction, not talent, is the gap.`,
      practiceThemes: ['Match Day'],
    });
  }

  const breakPtsSavedPct = pct(raw.BreakPtsSaved ?? 0, raw.BreakPtsFaced ?? 0);
  if (breakPtsSavedPct !== null && breakPtsSavedPct < 50) {
    insights.push({
      id: 'low-break-saved',
      message: `Saving only ${Math.round(breakPtsSavedPct)}% of break points faced.`,
      practiceThemes: ['Match Day'],
    });
  }

  return insights;
}
